import { HOST_URL } from '../../config'
import { HttpStatus } from '../../constants/http-status.enum'
import type { ApiResponse } from '../index'

// ==================== System Info ====================

export interface SystemInfo {
    appVersion: string
    idfVersion: string
    partName: string
    compileDate: string
    compileTime: string
    cores: number
    revision: number
    flashSize: number
    wifiMac: string
    btMac: string
}

export interface SystemInfoResponse {
    success: number
    appVersion: string
    idfVersion: string
    partName: string
    compileDate: string
    compileTime: string
    cores: number
    revision: number
    flashSize: number
    wifiMac: string
    btMac: string
    errorMsg?: string
}

export interface SystemStatus {
    uptimeMs: number
    freeHeap: number
    rtcTime: string
    rtcDate: string
    timezone: string
}

export interface SystemStatusResponse {
    success: number
    uptimeMs: number
    freeHeap: number
    rtcTime: string
    rtcDate: string
    timezone: string
    errorMsg?: string
}

// ==================== Brightness ====================

export interface BrightnessGetResponse {
    success: number
    brightness: number
}

export interface BrightnessSetResponse {
    success: number
    brightness: number
    errorMsg?: string
}

// ==================== Time ====================

export interface TimeSetRequest {
    year: number
    month: number
    day: number
    hour: number
    minute: number
}

export interface TimeSetResponse {
    success: number
    errorMsg: string
}

export interface TimeSyncResponse {
    success: number
    errorMsg: string
}

// ==================== Timezone ====================

export interface TimezoneInfo {
    index: number
    name: string
    offset: string
    posixTz: string
}

export interface TimezoneListResponse {
    success: number
    timezoneCount: number
    timezones: TimezoneInfo[]
    errorMsg?: string
}

export interface TimezoneGetResponse {
    success: number
    timezoneIndex: number
    timezoneName: string
    errorMsg?: string
}

export interface TimezoneSetResponse {
    success: number
    errorMsg?: string
}

// ==================== Language ====================

export interface LanguageGetResponse {
    success: number
    language: string
}

export interface LanguageSetResponse {
    success: number
    errorMsg: string
}

// ==================== System Restart ====================

export interface SystemRestartResponse {
    success: number
    errorMsg: string
}

export interface UserSecurityGetResponse {
    lockOnStartup: number
}

export interface UserSecuritySetResponse {
    success: number
    errorMsg?: string
}

export interface SystemLockResponse {
    success: number
    errorMsg?: string
}

export interface AdminAuthKeyResponse {
    success: number
    errorMsg: string
}

export interface AdminRelockResponse {
    success: number
    errorMsg: string
}

// ==================== API Functions ====================

export async function getSystemInfo(clientId: string): Promise<ApiResponse<SystemInfoResponse>> {
    const response = await fetch(`${HOST_URL}system/info`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ clientId }),
    })
    const result = (await response.json()) as ApiResponse<SystemInfoResponse>
    if (response.status !== HttpStatus.Ok) throw new Error(result.message)
    return result
}

export async function getSystemStatus(clientId: string): Promise<ApiResponse<SystemStatusResponse>> {
    const response = await fetch(`${HOST_URL}system/status`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ clientId }),
    })
    const result = (await response.json()) as ApiResponse<SystemStatusResponse>
    if (response.status !== HttpStatus.Ok) throw new Error(result.message)
    return result
}

export async function getBrightness(clientId: string): Promise<ApiResponse<BrightnessGetResponse>> {
    const response = await fetch(`${HOST_URL}system/brightness/get`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ clientId }),
    })
    const result = (await response.json()) as ApiResponse<BrightnessGetResponse>
    if (response.status !== HttpStatus.Ok) throw new Error(result.message)
    return result
}

export async function setBrightness(clientId: string, brightness: number): Promise<ApiResponse<BrightnessSetResponse>> {
    const response = await fetch(`${HOST_URL}system/brightness/set`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ clientId, brightness }),
    })
    const result = (await response.json()) as ApiResponse<BrightnessSetResponse>
    if (response.status !== HttpStatus.Ok) throw new Error(result.message)
    return result
}

export async function setTime(clientId: string, data: TimeSetRequest): Promise<ApiResponse<TimeSetResponse>> {
    const response = await fetch(`${HOST_URL}system/time/set`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ clientId, ...data }),
    })
    const result = (await response.json()) as ApiResponse<TimeSetResponse>
    if (response.status !== HttpStatus.Ok) throw new Error(result.message)
    return result
}

export async function syncTime(clientId: string): Promise<ApiResponse<TimeSyncResponse>> {
    const response = await fetch(`${HOST_URL}system/time/sync`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ clientId }),
    })
    const result = (await response.json()) as ApiResponse<TimeSyncResponse>
    if (response.status !== HttpStatus.Ok) throw new Error(result.message)
    return result
}

export async function getTimezoneList(clientId: string): Promise<ApiResponse<TimezoneListResponse>> {
    const response = await fetch(`${HOST_URL}system/timezone/list`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ clientId }),
    })
    const result = (await response.json()) as ApiResponse<TimezoneListResponse>
    if (response.status !== HttpStatus.Ok) throw new Error(result.message)
    return result
}

export async function getTimezone(clientId: string): Promise<ApiResponse<TimezoneGetResponse>> {
    const response = await fetch(`${HOST_URL}system/timezone/get`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ clientId }),
    })
    const result = (await response.json()) as ApiResponse<TimezoneGetResponse>
    if (response.status !== HttpStatus.Ok) throw new Error(result.message)
    return result
}

export async function setTimezone(clientId: string, posixTz: string): Promise<ApiResponse<TimezoneSetResponse>> {
    const response = await fetch(`${HOST_URL}system/timezone/set`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ clientId, posixTz }),
    })
    const result = (await response.json()) as ApiResponse<TimezoneSetResponse>
    if (response.status !== HttpStatus.Ok) throw new Error(result.message)
    return result
}

export async function getLanguage(clientId: string): Promise<ApiResponse<LanguageGetResponse>> {
    const response = await fetch(`${HOST_URL}system/language/get`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ clientId }),
    })
    const result = (await response.json()) as ApiResponse<LanguageGetResponse>
    if (response.status !== HttpStatus.Ok) throw new Error(result.message)
    return result
}

export async function setLanguage(clientId: string, language: string): Promise<ApiResponse<LanguageSetResponse>> {
    const response = await fetch(`${HOST_URL}system/language/set`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ clientId, language }),
    })
    const result = (await response.json()) as ApiResponse<LanguageSetResponse>
    if (response.status !== HttpStatus.Ok) throw new Error(result.message)
    return result
}

export async function restartSystem(clientId: string): Promise<ApiResponse<SystemRestartResponse>> {
    const response = await fetch(`${HOST_URL}system/restart`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ clientId }),
    })
    const result = (await response.json()) as ApiResponse<SystemRestartResponse>
    if (response.status !== HttpStatus.Ok) throw new Error(result.message)
    return result
}

export async function getUserSecurity(clientId: string): Promise<ApiResponse<UserSecurityGetResponse>> {
    const response = await fetch(`${HOST_URL}system/user-security/get`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ clientId }),
    })
    const result = (await response.json()) as ApiResponse<UserSecurityGetResponse>
    if (response.status !== HttpStatus.Ok) throw new Error(result.message)
    return result
}

export async function setUserSecurity(
    clientId: string,
    lockOnStartup: number,
    userPin: string,
): Promise<ApiResponse<UserSecuritySetResponse>> {
    const response = await fetch(`${HOST_URL}system/user-security/set`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ clientId, lockOnStartup, userPin }),
    })
    const result = (await response.json()) as ApiResponse<UserSecuritySetResponse>
    if (response.status !== HttpStatus.Ok) throw new Error(result.message)
    return result
}

export async function lockSystemNow(clientId: string): Promise<ApiResponse<SystemLockResponse>> {
    const response = await fetch(`${HOST_URL}system/lock`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ clientId }),
    })
    const result = (await response.json()) as ApiResponse<SystemLockResponse>
    if (response.status !== HttpStatus.Ok) throw new Error(result.message)
    return result
}

export async function adminAuthKey(clientId: string): Promise<ApiResponse<AdminAuthKeyResponse>> {
    const response = await fetch(`${HOST_URL}system/admin-auth/key`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ clientId }),
    })
    const result = (await response.json()) as ApiResponse<AdminAuthKeyResponse>
    if (response.status !== HttpStatus.Ok) throw new Error(result.message)
    return result
}

export async function adminRelock(clientId: string): Promise<ApiResponse<AdminRelockResponse>> {
    const response = await fetch(`${HOST_URL}system/admin-auth/relock`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ clientId }),
    })
    const result = (await response.json()) as ApiResponse<AdminRelockResponse>
    if (response.status !== HttpStatus.Ok) throw new Error(result.message)
    return result
}

// ==================== Settings Export/Import ====================

export interface SettingsExportResponse {
    success: number
    jsonData: string
    errorMsg?: string
}

export interface SettingsImportResponse {
    success: number
    errorMsg?: string
}

export interface SettingsProfileSummary {
    id: number
    name: string
    createdAt: string
}

export interface SettingsProfileData extends SettingsProfileSummary {
    jsonData: string
}

export async function exportSettings(clientId: string): Promise<ApiResponse<SettingsExportResponse>> {
    const response = await fetch(`${HOST_URL}system/settings/export`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ clientId }),
    })
    const result = (await response.json()) as ApiResponse<SettingsExportResponse>
    if (response.status !== HttpStatus.Ok) throw new Error(result.message)
    return result
}

export async function importSettings(clientId: string, jsonData: string): Promise<ApiResponse<SettingsImportResponse>> {
    const response = await fetch(`${HOST_URL}system/settings/import`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ clientId, jsonData }),
    })
    const result = (await response.json()) as ApiResponse<SettingsImportResponse>
    if (response.status !== HttpStatus.Ok) throw new Error(result.message)
    return result
}

export async function saveSettingsProfileFromMachine(clientId: string, name: string): Promise<ApiResponse<SettingsProfileData>> {
    const response = await fetch(`${HOST_URL}system/settings/profiles/save-export`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ clientId, name }),
    })
    const result = (await response.json()) as ApiResponse<SettingsProfileData>
    if (response.status !== HttpStatus.Ok) throw new Error(result.message)
    return result
}

export async function saveSettingsProfile(name: string, jsonData: string): Promise<ApiResponse<SettingsProfileSummary>> {
    const response = await fetch(`${HOST_URL}system/settings/profiles/save`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ name, jsonData }),
    })
    const result = (await response.json()) as ApiResponse<SettingsProfileSummary>
    if (response.status !== HttpStatus.Ok) throw new Error(result.message)
    return result
}

export async function listSettingsProfiles(): Promise<ApiResponse<SettingsProfileSummary[]>> {
    const response = await fetch(`${HOST_URL}system/settings/profiles`, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
    })
    const result = (await response.json()) as ApiResponse<SettingsProfileSummary[]>
    if (response.status !== HttpStatus.Ok) throw new Error(result.message)
    return result
}

export async function getSettingsProfile(name: string): Promise<ApiResponse<SettingsProfileData>> {
    const response = await fetch(`${HOST_URL}system/settings/profiles/get`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ name }),
    })
    const result = (await response.json()) as ApiResponse<SettingsProfileData>
    if (response.status !== HttpStatus.Ok) throw new Error(result.message)
    return result
}
